import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, StatusBadge, SectionCard } from "@/components/common/Primitives";
import { KpiCard } from "@/components/common/KpiCard";
import { fmtEUR, fmtDate } from "@/lib/format";
import { RENEWAL_STAGE_MAP } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RotateCw, AlertTriangle, UserX, CalendarClock, Send } from "lucide-react";

export default function Renewals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["renewals"], queryFn: async () => (await api.get("/renewals")).data });

  const setStage = async (cid, status) => {
    try { await api.post(`/renewals/${cid}/stage`, { renewal_status: status }); toast.success("Phase aktualisiert"); qc.invalidateQueries({ queryKey: ["renewals"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (isLoading) return <PageHeader title="Vertragsverlängerungen" />;
  const buckets = data?.buckets || {};
  const alerts = data?.alerts || {};
  const stages = data?.stages || {};
  const pipelineOrder = ["anstehend", "kontaktversuch", "kontakt_erfolgreich", "angebot_gesendet", "nachfassen", "verlaengert", "verloren"];

  const Card = ({ c }) => (
    <div className="rounded-md border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <button onClick={() => navigate(`/vertraege/${c.id}`)} className="min-w-0 text-left">
          <div className="truncate text-sm font-medium hover:underline">{c.customer_name}</div>
          <div className="truncate text-xs text-muted-foreground">{c.provider} · {c.tariff_name}</div>
        </button>
        <span className={daysCls(c.days_to_end)}>{c.days_to_end ?? "—"}T</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">{fmtEUR(c.monthly_fee)}/M</span>
        {can(user?.role, "renewals:write") ? (
          <Select value={c.renewal_status} onValueChange={(v) => setStage(c.id, v)}>
            <SelectTrigger className="h-7 w-36 text-xs" data-testid={`renewal-stage-${c.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{pipelineOrder.map((s) => <SelectItem key={s} value={s} className="text-xs">{RENEWAL_STAGE_MAP[s].label}</SelectItem>)}</SelectContent>
          </Select>
        ) : <StatusBadge status={c.renewal_status} map={RENEWAL_STAGE_MAP} />}
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Vertragsverlängerungen" description="Pipeline, Fristen und automatisch erzeugte Aufgaben" />

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard testId="renewal-kpi-30" label="≤ 30 Tage" value={(buckets.d30 || []).length} icon={AlertTriangle} tone="danger" />
        <KpiCard testId="renewal-kpi-60" label="≤ 60 Tage" value={(buckets.d60 || []).length} icon={CalendarClock} tone="warning" />
        <KpiCard testId="renewal-kpi-90" label="≤ 90 Tage" value={(buckets.d90 || []).length} icon={CalendarClock} tone="info" />
        <KpiCard testId="renewal-kpi-180" label="≤ 180 Tage" value={(buckets.d180 || []).length} icon={RotateCw} />
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-4">
        <AlertBox title="Nicht zugewiesen" icon={UserX} rows={alerts.unassigned} navigate={navigate} />
        <AlertBox title="Ungeprüfte Fristen" icon={AlertTriangle} rows={alerts.unverified_dates} navigate={navigate} />
        <AlertBox title="Angebote warten" icon={Send} rows={alerts.offers_waiting} navigate={navigate} />
        <AlertBox title="Überfällige Nachfassung" icon={CalendarClock} rows={alerts.overdue_followup} navigate={navigate} />
      </div>

      <h2 className="mb-3 text-lg font-semibold tracking-tight">Pipeline</h2>
      <div className="grid gap-3 overflow-x-auto md:grid-cols-2 xl:grid-cols-4">
        {pipelineOrder.map((stage) => (
          <div key={stage} className="rounded-lg border border-border bg-muted/20 p-2" data-testid={`pipeline-column-${stage}`}>
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-xs font-semibold">{RENEWAL_STAGE_MAP[stage].label}</span>
              <Badge variant="secondary" className="text-[10px]">{(stages[stage] || []).length}</Badge>
            </div>
            <div className="space-y-2">
              {(stages[stage] || []).length === 0 && <p className="px-1 py-3 text-center text-[11px] text-muted-foreground">—</p>}
              {(stages[stage] || []).map((c) => <Card key={c.id} c={c} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertBox({ title, icon: Icon, rows = [], navigate }) {
  return (
    <SectionCard title={<span className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" />{title} ({rows.length})</span>}>
      <div className="space-y-1.5">
        {rows.length === 0 && <p className="py-2 text-center text-xs text-muted-foreground">Keine</p>}
        {rows.slice(0, 5).map((c) => (
          <button key={c.id} onClick={() => navigate(`/vertraege/${c.id}`)} className="block w-full truncate rounded px-2 py-1 text-left text-xs hover:bg-muted">
            {c.customer_name} · {c.provider}
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

function daysCls(d) {
  const base = "rounded px-1.5 py-0.5 text-[11px] font-semibold ";
  if (d == null) return base + "bg-muted text-muted-foreground";
  if (d <= 30) return base + "bg-destructive/10 text-destructive";
  if (d <= 60) return base + "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  return base + "bg-muted text-muted-foreground";
}
